<template>
  <div
    class="inbox-content"
    @keydown.esc.exact.prevent="emit('cancel')"
  >
    <ThankYou
      v-if="formSubmitted && thankYou"
      @close="handleCancel"
    />
    <div
      v-else
      class="contact-form"
    >
      <div class="form-group">
        <label for="name">Name <span class="required">*</span></label>
        <input
          id="name"
          v-model="v$.form.name.$model"
          type="text"
          placeholder="Full name"
          aria-required="true"
          required
          :class="{ error: v$.form.name.$error }"
        >
        <span
          v-if="v$.form.name.$error && formSubmitted"
          class="input-error-message"
        >Name is required.</span>
      </div>

      <div class="form-group">
        <label for="email">E-mail address <span class="required">*</span></label>
        <input
          id="email"
          v-model="v$.form.email.$model"
          type="email"
          placeholder="E-mail"
          aria-required="true"
          required
          :class="{ error: v$.form.email.$error && formSubmitted }"
        >
        <span
          v-if="v$.form.email.$error && formSubmitted"
          class="input-error-message"
        >{{ v$.form.email.required.$invalid ? "Email is required." : "Email is invalid. Please enter a valid email address." }}</span>
      </div>

      <div class="form-group">
        <label id="school">School</label>
        <MsgSelect
          v-model="form.school"
          :options="clientInfo.organizations"
          form-group-label-id="school"
          track-by="id"
          label-by="name"
          placeholder="Select an organization"
          searchable
          search-placeholder="Search schools..."
          no-options-text="No schools found"
        />
      </div>

      <div class="form-group">
        <label
          id="topic"
        >Topics</label>
        <MsgSelect
          v-model="form.topic"
          :options="clientInfo.contact_topics"
          form-group-label-id="topic"
          grouped-by="category"
          track-by="value"
          label-by="name"
          no-options-text="No topics available"
          placeholder="Select a topic"
        />
      </div>

      <div class="form-group">
        <label for="description">Description <span class="required">*</span></label>
        <textarea
          id="description"
          v-model="form.description"
          placeholder="Please describe your request here"
          rows="4"
          required
          aria-required="true"
          :class="{ error: v$.form.description.$error }"
        />
        <div
          class="description-info-wrapper"
          :class="{ error: v$.form.description.$error }"
        >
          <span
            v-if="v$.form.description.$error && formSubmitted"
            class="description-error-message"
          >{{ v$.form.description.maxLength.$invalid ? "Description must be less than 100 characters." : "Description is required." }}</span>
          <div class="character-count">
            {{ form.description.length }}/100
          </div>
        </div>
      </div>

      <FileAttachments
        v-model="form.attachments"
      />

      <div class="form-actions">
        <MsgButton
          type="secondary"
          @click.prevent="handleCancel"
        >
          Cancel
        </MsgButton>
        <MsgButton
          type="primary"
          :disabled="submitButtonDisabled"
          @click.prevent="handleSubmit"
        >
          Submit
        </MsgButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email, maxLength } from "@vuelidate/validators";
import { useScrollLock } from "@vueuse/core";
import ThankYou from "./ThankYou.vue";
import FileAttachments from "../FileAttachments.vue";
import MsgSelect from "../../MsgUi/MsgSelect.vue";
import MsgButton from "../../MsgUi/MsgButton.vue";
defineProps({
  clientInfo: {
    type: Object,
    required: true
  }
});
const emit = defineEmits(["submit", "cancel", "validationError"]);
const form = ref({
  name: "",
  email: "",
  school: "",
  topic: "",
  description: "",
  attachments: []
});
const formSubmitted = ref(false);
const thankYou = ref(false);
const rules = computed(() => ({
  form: {
    name: { required },
    email: { required, email },
    description: { required, maxLength: maxLength(100) }
  }
}));
const submitButtonDisabled = computed(() => {
  return formSubmitted.value && v$.value.$errors.length > 0;
});
const v$ = useVuelidate(rules, { form });
async function handleSubmit() {
  formSubmitted.value = true;
  const isFormValid = await v$.value.$validate();
  if (isFormValid) {
    thankYou.value = true;
    emit("submit", form.value);
  } else {
    const errorProperty = v$.value.$errors[0].$property;
    const errorElement = document.querySelector(`#${errorProperty}`);
    if (errorElement instanceof HTMLElement) {
      errorElement.focus({ preventScroll: true });
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    emit("validationError", v$.value.$errors);
  }
}
function handleCancel() {
  formSubmitted.value = false;
  thankYou.value = false;
  emit("cancel");
}
onMounted(() => {
  useScrollLock(document.body, true);
});
onUnmounted(() => {
  useScrollLock(document.body, false);
});
</script>

<style scoped>
.inbox-content{height:100%;overflow-y:auto;padding:24px;scrollbar-width:thin;width:470px;--border-radius-xs:4px;--border-radius-full:9999px;--color-border-default:#adadad;--color-blue-75:#e6edfa;--color-blue-300:#6184ca;--color-blue-500:#1850c1;--color-blue-500-hover:#1444a4;--color-grey-100:#e0e0e0;--color-grey-950:#2d2d2d;--color-red-600:#bd3324;--color-surface-danger-default:#fdf5f4;--color-text-secondary:#616161;--color-text-severity-danger-on-bg:#9b2a1d;--color-white:#fff;--space-200:8px;--space-300:12px;--space-400:16px;--space-600:24px;--space-1000:40px}.contact-form{align-self:stretch;gap:var(--space-600)}.contact-form,.form-group{display:flex;flex-direction:column}.form-group{gap:6px;position:relative}.form-group label{color:var(--color-grey-950);font-size:16px;font-style:normal;font-weight:400;line-height:22px;width:-moz-fit-content;width:fit-content}.form-group label .required{color:var(--color-red-600)}.form-group input,.form-group textarea{background:var(--color-white);border:1px solid var(--color-border-default);border-radius:var(--border-radius-xs);font-family:Inter;font-size:16px;font-weight:400;line-height:22px;padding:var(--space-300);transition:border-color .2s}.form-group textarea{color:var(--color-text-secondary);resize:vertical}.form-group input:focus,.form-group textarea:focus{border:1px solid var(--color-blue-500)}#description.error,.form-group input.error{background:var(--color-surface-danger-default);border:1px solid var(--color-text-severity-danger-on-bg);color:var(--color-text-severity-danger-on-bg)}input.error::-moz-placeholder,textarea.error::-moz-placeholder{color:var(--color-red-600)}input.error::placeholder,textarea.error::placeholder{color:var(--color-red-600)}input.error:focus,textarea.error:focus{border:1px solid var(--color-text-severity-danger-on-bg);outline:1px solid var(--color-text-severity-danger-on-bg)}.description-error-message,.form-group .input-error-message{color:var(--color-text-severity-danger-on-bg);font-size:12px;font-weight:400;letter-spacing:.4px}.form-group .input-error-message{position:absolute;transform:translateY(80px)}.description-info-wrapper{align-items:center;display:flex;flex-direction:row;justify-content:flex-end}.description-info-wrapper.error{justify-content:space-between}.character-count{color:var(--color-text-secondary);font-size:12px;text-align:right}.form-actions{display:flex;gap:12px;margin-top:8px}.fade-enter-active,.fade-leave-active{transition:all .3s cubic-bezier(.4,0,.2,1)}
</style>
